import React, { useState, useEffect } from "react";
import Joyride, { Step, CallBackProps, STATUS } from "react-joyride";
import { useLocation } from "react-router-dom";
import "../../Assets/scss/components/TourGuide.scss";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useSelector, useDispatch } from "react-redux";
import { updateUserProfileApi } from "../../Api/UserAction/UpdateUserDetails";
import { UPDATE_USER } from "../../Actions/Types";

const TourGuide: React.FC = () => {
    const [run, setRun] = useState(false);
    const { currentWebsite } = useSelector((store: StoreProptypes) => store);
    const location = useLocation();
    const dispatch = useDispatch();
    const user = useSelector((store: StoreProptypes) => store.user);
    const { accessToken } = useSelector((store: StoreProptypes) => store);

    useEffect(() => {
        if (run) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [run]);

    useEffect(() => {
        if (currentWebsite && currentWebsite?.is_eu_script_show_first === 1) {
            setRun(false)
        }
    }, [currentWebsite, run])

    const steps: Step[] = [
        {
            target: "body",
            content: (
                <div className="tour-intro">
                    <i className="material-symbols-outlined" aria-hidden="true">
                        emoji_people
                    </i>
                    <h2>👋 Welcome!</h2>
                    <p>Let’s walk you through the main features of your dashboard.</p>
                </div>
            ),
            placement: "center",
        },
        {
            target: ".tour-logo",
            content: (
                <div>
                    <i className="material-symbols-outlined" aria-hidden="true">
                        accessibility_new
                    </i>
                    <h4>Accessibility Logo</h4>
                    <p>
                        This is the <strong>All in One Accessibility</strong> logo. Click it
                        anytime to return to your main dashboard.
                    </p>
                </div>
            ),
        },
        {
            target: ".tour-plan-type",
            content: (
                <div>
                    <i className="material-symbols-outlined" aria-hidden="true">
                        workspace_premium
                    </i>
                    <h4>Plan Type</h4>
                    <p>
                        Here you can view your <strong>current subscription plan</strong>{" "}
                        and upgrade if needed.
                    </p>
                </div>
            ),
            placement: "right",
        },
        {
            target: ".tour-sidebar",
            content: (
                <div>
                    <i className="material-symbols-outlined" aria-hidden="true">
                        menu
                    </i>
                    <h4>Sidebar Navigation</h4>
                    <p>
                        Use the sidebar to <strong>navigate between sections</strong> like
                        Dashboard, Domains, Settings, and more.
                    </p>
                </div>
            ),
            placement: "right",
        },
        {
            target: ".tour-Website",
            content: (
                <div>
                    <i className="material-symbols-outlined" aria-hidden="true">
                        language
                    </i>
                    <h4>Default Domain</h4>
                    <p>
                        This shows your <strong>primary website/domain</strong> connected to
                        accessibility services.
                    </p>
                </div>
            ),
        },
        {
            target: ".tour-Add-new-domain",
            content: (
                <div>
                    <i className="material-symbols-outlined" aria-hidden="true">
                        add_circle
                    </i>
                    <h4>Add New Domain</h4>
                    <p>
                        Click here to <strong>add a new website</strong> and start managing
                        its accessibility features.
                    </p>
                </div>
            ),
        },
        {
            target: ".tour-language",
            content: (
                <div>
                    <i className="material-symbols-outlined" aria-hidden="true">
                        translate
                    </i>
                    <h4>Language</h4>
                    <p>
                        Easily <strong>switch dashboard language</strong> for better
                        accessibility and convenience.
                    </p>
                </div>
            ),
        },
        {
            target: ".tour-profile",
            content: (
                <div>
                    <i className="material-symbols-outlined" aria-hidden="true">
                        account_circle
                    </i>
                    <h4>Profile</h4>
                    <p>
                        Manage your <strong>account settings</strong>, security options, and
                        personal details from here.
                    </p>
                </div>
            ),
        },
        {
            target: ".tour-title",
            content: (
                <div>
                    <i className="material-symbols-outlined" aria-hidden="true">
                        dashboard
                    </i>
                    <h4>Dashboard</h4>
                    <p>
                        This is your <strong>dashboard overview</strong> where you can track
                        key accessibility insights.
                    </p>
                </div>
            ),
            placement: "bottom",
            spotlightPadding: 10,
        },
        {
            target: ".tour-scripts",
            content: (
                <div>
                    <i className="material-symbols-outlined" aria-hidden="true">
                        code
                    </i>
                    <h4>Installation Script</h4>
                    <p>
                        Copy this <strong>installation script</strong> and paste it into
                        your website to enable accessibility features.
                    </p>
                </div>
            ),
            placement: "top",
        },
        {
            target: ".tour-Support",
            content: (
                <div>
                    <i className="material-symbols-outlined" aria-hidden="true">
                        support_agent
                    </i>
                    <h4>Support</h4>
                    <p>
                        Need help? Visit the <strong>Support Center</strong> to explore
                        FAQs, guides, or contact our team for quick assistance.
                    </p>
                </div>
            ),
            placement: "top",
        },
    ];

    useEffect(() => {
        if (location.pathname === "/front" && user?.is_tour_show === 0) {
            const hasSeenTour = localStorage.getItem("is_tour_show");
            if (!hasSeenTour) {
                setRun(true);
                localStorage.setItem("is_tour_show", String(user?.is_tour_show));
            }
        }
    }, [location]);

    const handleCallback = async  (data: CallBackProps)  => {
        const { status, action  } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

        if ( action === "close" || finishedStatuses.includes(status)) {
            setRun(false);
            // localStorage.setItem("is_tour_show", "true");
            if (!user || !accessToken || user?.is_tour_show === 1) return;
            try {
                const response = await updateUserProfileApi(
                    { ...user, is_tour_show: 1 },
                    accessToken
                );
                setRun(false);
                if (response?.Data) {
                    dispatch({
                        type: UPDATE_USER,
                        payload: { user: response.Data },
                    });
                }
            } catch (err) {
                console.error("Failed to update tour status:", err);
            }
        }
    };

    return (
        <Joyride
            steps={steps}
            run={run}
            continuous
            showProgress
            showSkipButton
            spotlightClicks={false}
            disableScrollParentFix={true}
            callback={handleCallback}
            scrollOffset={120}
            locale={{
                back: "◀ Back",
                close: "Got it!",
                last: "Finish",
                next: "Next ▶",
                skip: "Skip Tour",
            }}
            styles={{
                options: {
                    primaryColor: "#420083",
                    backgroundColor: "#fff",
                    textColor: "#333",
                    zIndex: 10000,
                },
            }}
        />
    );
};

export default TourGuide;
